import { useState } from 'react';

import { Button, Group, Stack, Text } from '@mantine/core';

import { MessageBubble } from '@/components/Chat/MessageBubble';
import type { ChatMessage } from '@/types/chat';
import {
  canReplyToRegistryMessage,
  mayHaveRegistryDocuments,
  type RegistryConversationItem,
  type RegistryOrderMessage,
} from '@/types/registry-documents';
import {
  formatRegistryMessageTime,
  OrderLabel,
  RegistryMessageBadges,
  TRUNCATED_NOTE,
  UnverifiedSenderNote,
} from '../RegistryOrdersCard/RegistryOrderMessages';
import {
  conversationSystemText,
  deliveryText,
  isDelivery,
  type ConversationEntry,
} from './conversationEntries';
import { OrderFiles, OrderFilesButton } from './ConversationOrders';
import { replyTargetKey } from './conversationTargets';
import classes from './RegistryConversation.module.css';

/**
 * One entry of the Armadillo thread: a chat bubble for an email either
 * way, with the order it is on and what it is flagged with on its side; a
 * delivery line for files that arrived; a dimmed divider for an order
 * sent, a quote recorded or an order closed.
 *
 * The emails are rendered as the order card renders them: as text only,
 * with their links never made clickable.
 */
export function ConversationEntryView({
  entry,
  onReply,
}: Readonly<{
  entry: ConversationEntry;
  /** Point the composer at one of the desk's emails, by its target key.
   * Omitted where replying is not offered. */
  onReply?: (targetKey: string) => void;
}>) {
  const { message, chat } = entry;
  if (!message) {
    return <SystemLine item={entry.item} />;
  }
  return (
    <Stack role="listitem" gap={2} data-direction={message.direction} data-kind="message">
      {chat && <Bubble entry={entry} message={message} chat={chat} onReply={onReply} />}
      {isDelivery(message) && <DeliveryLine entry={entry} message={message} />}
    </Stack>
  );
}

function SystemLine({ item }: Readonly<{ item: RegistryConversationItem }>) {
  const when = formatRegistryMessageTime(item.at);
  return (
    <div role="listitem" className={classes.system} data-kind={item.kind}>
      <Text size="xs" c="dimmed" ta="center">
        {conversationSystemText(item)}
        {when ? ` · ${when}` : ''}
      </Text>
    </div>
  );
}

/** The email as a bubble: the Hub's replies on the right, the desk's on
 * the left, each under its order's reference. A desk email carries its
 * badges and, where the API would take one, the way to answer it; a Hub
 * reply names the colleague who sent it. */
function Bubble({
  entry,
  message,
  chat,
  onReply,
}: Readonly<{
  entry: ConversationEntry;
  message: RegistryOrderMessage;
  chat: ChatMessage;
  onReply?: (targetKey: string) => void;
}>) {
  const { item, order, isMine, groupedWithPrev } = entry;
  const side = isMine ? 'flex-end' : 'flex-start';
  const facts = [formatRegistryMessageTime(message.receivedAt), message.sender?.trim()]
    .filter(Boolean)
    .join(' · ');
  // Reply is offered where the API would accept one to this email: never
  // to the Hub's own, never to an unverified sender.
  const replyKey =
    !isMine && order && onReply && canReplyToRegistryMessage(order, message)
      ? replyTargetKey(order.id, message.messageId)
      : null;
  const notes = message.truncated || !message.senderVerified;

  return (
    <Stack gap={2} align={side}>
      <Group gap={6} wrap="wrap" justify={side} className={classes.chips}>
        <OrderLabel label={item.reference} />
        {isMine ? (
          <Text size="xs" c="dimmed">
            {chat.senderDisplayName}
          </Text>
        ) : (
          <RegistryMessageBadges message={message} orderCountryCode={order?.countryCode ?? null} />
        )}
        {replyKey && (
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() => onReply?.(replyKey)}
            aria-label={`Reply to the message${facts ? ` of ${facts}` : ''}`}
          >
            Reply
          </Button>
        )}
      </Group>
      <MessageBubble message={chat} isMine={isMine} groupedWithPrev={groupedWithPrev} />
      {notes && (
        <Stack gap={0} align={side} className={classes.notes}>
          {!message.senderVerified && <UnverifiedSenderNote />}
          {message.truncated && (
            <Text size="xs" c="dimmed">
              {TRUNCATED_NOTE}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** Files that arrived: one quiet line on the desk's side, with the order's
 * files a click away, read only once asked for. */
function DeliveryLine({
  entry,
  message,
}: Readonly<{ entry: ConversationEntry; message: RegistryOrderMessage }>) {
  const { item, order, key } = entry;
  const [open, setOpen] = useState(false);
  const when = formatRegistryMessageTime(message.receivedAt);
  const files = order && mayHaveRegistryDocuments(order.status);
  return (
    <Stack gap={2} align="flex-start" className={classes.delivery} data-kind="delivery">
      <Group gap="xs" wrap="wrap">
        <Text size="xs" c="dimmed">
          {deliveryText(item, message)}
          {when ? ` · ${when}` : ''}
        </Text>
        {files && (
          <OrderFilesButton
            order={order}
            open={open}
            onToggle={() => setOpen((current) => !current)}
            data-testid={`registry-conversation-delivery-files-toggle-${key}`}
          />
        )}
      </Group>
      {files && open && (
        <OrderFiles order={order} data-testid={`registry-conversation-delivery-files-${key}`} />
      )}
    </Stack>
  );
}
